import { useState, useEffect, useRef } from "react"
import type { KeyboardEvent } from "react"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Package, Plus, X, Tag, Loader2, ChevronRight, ChevronLeft,
  Hash, Layers, ArrowLeft, FileText, Calendar, Edit2, AlertCircle, MoreVertical, ChevronDown
} from "lucide-react"
import { useProductDetails } from "@/hooks/seller/useProductDetails"
import { useAddVariant } from "@/hooks/seller/useAddVariant"
import { useUpdateVariant } from "@/hooks/seller/useUpdateVariant"
import { useProductVariantDetails } from "@/hooks/seller/useProductVariantDetails"
import { useCountryCurrencies } from "@/hooks/useCountryCurrencies"
import { variantSchema, type VariantFormValues } from "@/lib/schemas/product.schema"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { Variant, ProductImage } from "@/lib/api/product.api"
import { useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { useSellerUIStore } from "@/store/seller-ui.store"
import { AddProductForm, inputCls, Field } from "./AddProductForm"

export function ProductDetailsPage({ productId }: { productId: string }) {
  const navigate = useNavigate()
  const handleGoBack = () => {
    navigate({ to: "/products" })
  }

  return (
    <ProductDetailView
      productId={productId}
      onBack={handleGoBack}
    />
  )
}

function VariantCard({ variant, onViewDetail }: { variant: Variant, onViewDetail: () => void }) {
  let formattedPrice: string | null = null
  if (variant.price !== undefined) {
    try {
      const currency = (variant.currencyCode && variant.currencyCode !== "string")
        ? variant.currencyCode.toUpperCase()
        : "INR"
      formattedPrice = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(variant.price)
    } catch (e) {
      formattedPrice = `${variant.currencyCode && variant.currencyCode !== "string" ? variant.currencyCode.toUpperCase() : "INR"} ${variant.price}`
    }
  }

  return (
    <div className="group relative flex flex-col justify-between rounded-xl border border-slate-200/60 bg-white p-5 shadow-sm hover:border-[#be001e]/50 transition-all duration-200 pb-16">
      <div>
        <div className="absolute bottom-4 left-4 right-4 z-10">
          <button onClick={onViewDetail} className="w-full justify-center px-3 py-1.5 bg-[#be001e] hover:bg-[#e6202f] text-white rounded-lg border border-[#be001e] hover:border-[#e6202f] cursor-pointer flex items-center gap-1.5 text-xs font-bold transition-colors shadow-sm" title="View Detail">
            Detail
          </button>
        </div>
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="font-bold text-[#291716] text-sm break-words">{variant.name || "Unnamed Variant"}</h4>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#fff0ef]/50 border-[0.5px] border-[#e7bdb9]/50 rounded mt-1.5 text-[10px] text-slate-500 break-all">
              SKU: {variant.sku}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-base font-extrabold text-[#be001e]">{formattedPrice}</p>
            {variant.stockQuantity !== undefined && (
              <p className="text-[10px] font-medium text-[#5d3f3d] uppercase mt-0.5">
                Stock:{" "}
                <span className={variant.stockQuantity > 0 ? "text-emerald-600 font-bold" : "text-rose-600 font-bold"}>
                  {variant.stockQuantity}{variant.measureUnit || variant.unit ? ` ${variant.measureUnit || variant.unit}` : ""}
                </span>
              </p>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3 line-clamp-2 leading-relaxed border-t border-slate-100 pt-3 break-words whitespace-normal">{variant.description || "No description provided."}</p>
      </div>

      <div className="mt-4 flex justify-between items-center pt-3 border-t-[0.5px] border-slate-100 text-[10px]">
        <div className="flex items-center gap-1 text-[#5d3f3d]">
          <FileText className="size-3 text-[#be001e]" />
          <span>HSN: <strong>{variant.hsnCode || "None"}</strong></span>
        </div>
        {variant.tags && variant.tags.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1.5 max-w-[60%]">
            {variant.tags.map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-[#fff0ef] border-[0.5px] border-[#e7bdb9] rounded text-[9px] uppercase font-bold text-[#be001e]">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


const QUANTITY_UNITS = [
  { code: "pcs", name: "Pieces (pcs)" },
  { code: "box", name: "Box (box)" },
  { code: "kg", name: "Kilogram (kg)" },
  { code: "g", name: "Gram (g)" },
  { code: "ltr", name: "Liter (ltr)" },
  { code: "ml", name: "Milliliter (ml)" },
  { code: "qtl", name: "Quintal (qtl)" },
  { code: "ton", name: "Metric Ton (ton)" },
]

const isIntegerUnit = (unit?: string | null): boolean => {
  if (!unit) return false
  const u = unit.toLowerCase().trim()
  return u === "pcs" || u === "box" || u.includes("piece") || u.includes("box")
}

function AddVariantForm({
  productId,
  product,
  variantToEdit,
  onClose,
}: {
  productId: string
  product?: any
  variantToEdit?: Variant
  onClose: () => void
}) {
  const addVariant = useAddVariant(productId)
  const updateVariant = useUpdateVariant(productId, variantToEdit?.id || "")
  const [tagInput, setTagInput] = useState("")
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false)
  const [currencySearch, setCurrencySearch] = useState("")
  const currencyDropdownRef = useRef<HTMLDivElement>(null)
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false)
  const unitDropdownRef = useRef<HTMLDivElement>(null)

  const { data: countryCurrenciesData, isLoading: isLoadingCurrencies } = useCountryCurrencies()

  const currencyOptions = (countryCurrenciesData && countryCurrenciesData.length > 0)
    ? countryCurrenciesData.map((item) => ({
        id: item.id,
        code: item.code,
        country: item.country,
        name: `${item.code} - ${item.country}`,
      }))
    : [
        { id: 105, code: "INR", country: "INDIA", name: "INR - INDIA" },
        { id: 2, code: "USD", country: "UNITED STATES", name: "USD - UNITED STATES" },
        { id: 3, code: "EUR", country: "EUROPEAN UNION", name: "EUR - EUROPEAN UNION" },
        { id: 4, code: "GBP", country: "UNITED KINGDOM", name: "GBP - UNITED KINGDOM" },
        { id: 5, code: "AED", country: "UNITED ARAB EMIRATES", name: "AED - UNITED ARAB EMIRATES" },
        { id: 6, code: "SGD", country: "SINGAPORE", name: "SGD - SINGAPORE" },
      ]

  const filteredCurrencies = currencyOptions.filter((curr) => {
    if (!currencySearch.trim()) return true
    const q = currencySearch.toLowerCase().trim()
    return curr.code.toLowerCase().includes(q) || curr.name.toLowerCase().includes(q)
  })

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target as Node)) {
        setIsCurrencyDropdownOpen(false)
        setCurrencySearch("")
      }
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(e.target as Node)) {
        setIsUnitDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [])

  const blockQuantityKeys = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentVal: string | number | undefined | null,
    isIntegerOnly: boolean
  ) => {
    if (
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "Tab" ||
      e.key === "Escape" ||
      e.key === "Enter" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      (e.ctrlKey || e.metaKey)
    ) {
      return
    }
    if (isIntegerOnly && (e.key === "." || e.key === ",")) {
      e.preventDefault()
      return
    }
    if (e.key === "." && String(currentVal ?? "").includes(".")) {
      e.preventDefault()
      return
    }
    if (!/^[0-9.]$/.test(e.key)) {
      e.preventDefault()
    }
  }

  const blockFloatKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "Tab" ||
      e.key === "Escape" ||
      e.key === "Enter" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      (e.ctrlKey || e.metaKey)
    ) {
      return
    }
    if (e.key === "." && e.currentTarget.value.includes(".")) {
      e.preventDefault()
      return
    }
    if (!/^[0-9.]$/.test(e.key)) {
      e.preventDefault()
    }
  }

  const { register, handleSubmit, control, formState: { errors }, watch, setValue, trigger } =
    useForm<VariantFormValues>({
      mode: "onChange",
      resolver: zodResolver(variantSchema) as any,
      defaultValues: variantToEdit ? {
        sku: variantToEdit.sku,
        name: variantToEdit.name || "",
        description: variantToEdit.description || "",
        hsnCode: variantToEdit.hsnCode || "",
        price: variantToEdit.price,
        currencyCode: variantToEdit.currencyCode || "",
        stockQuantity: (() => {
          const u = variantToEdit.measureUnit || variantToEdit.unit || ""
          if (isIntegerUnit(u) && variantToEdit.stockQuantity !== undefined && variantToEdit.stockQuantity !== null) {
            const str = String(variantToEdit.stockQuantity)
            return str.includes(".") ? Number(str.split(".")[0]) : variantToEdit.stockQuantity
          }
          return variantToEdit.stockQuantity
        })(),
        unit: variantToEdit.measureUnit || variantToEdit.unit || "",
        tags: variantToEdit.tags || [],
      } : {
        sku: product?.productCode || "",
        name: product?.productName || "",
        description: product?.description || "",
        hsnCode: "",
        price: undefined,
        currencyCode: "",
        stockQuantity: undefined,
        unit: "",
        tags: product?.tags || [],
      },
    })

  useEffect(() => {
    if (variantToEdit) {
      setValue("sku", variantToEdit.sku)
      setValue("name", variantToEdit.name || "")
      setValue("description", variantToEdit.description || "")
      setValue("hsnCode", variantToEdit.hsnCode || "")
      setValue("price", variantToEdit.price)
      setValue("currencyCode", variantToEdit.currencyCode || "")
      const u = variantToEdit.measureUnit || variantToEdit.unit || ""
      let stockQty = variantToEdit.stockQuantity
      if (isIntegerUnit(u) && stockQty !== undefined && stockQty !== null) {
        const str = String(stockQty)
        if (str.includes(".")) {
          stockQty = Number(str.split(".")[0])
        }
      }
      setValue("stockQuantity", stockQty)
      setValue("unit", u)
      setValue("tags", variantToEdit.tags || [])
    } else if (product) {
      setValue("sku", product.productCode || "")
      setValue("name", product.productName || "")
      setValue("description", product.description || "")
      setValue("unit", "")
      setValue("tags", product.tags || [])
    }
  }, [product, variantToEdit, setValue])

  const tags = watch("tags") ?? []

  const addTag = () => {
    const t = tagInput.trim()
    if (t && !tags.includes(t) && tags.length < 20) {
      setValue("tags", [...tags, t], { shouldValidate: true })
      setTagInput("")
    }
  }

  const removeTag = (tag: string) =>
    setValue("tags", tags.filter((t) => t !== tag), { shouldValidate: true })

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag() }
  }

  const onInvalid = () => {
    const priceVal = watch("price")
    const currVal = watch("currencyCode")
    if (priceVal !== undefined && priceVal !== null && !currVal) {
      toast.error("Currency not selected")
    }
    const stockQty = watch("stockQuantity")
    const unitVal = watch("unit")
    if (stockQty !== undefined && stockQty !== null && !unitVal) {
      toast.error("Unit not selected")
    }
    if (unitVal && (stockQty === undefined || stockQty === null)) {
      toast.error("Stock quantity required when unit is selected")
    }
  }

  const onSubmit = (values: VariantFormValues) => {
    if (values.price !== undefined && values.price !== null && !values.currencyCode) {
      toast.error("Currency not selected")
      return
    }
    if (values.currencyCode && (values.price === undefined || values.price === null)) {
      toast.error("Price required when currency is selected")
      return
    }
    if (values.stockQuantity !== undefined && values.stockQuantity !== null && !values.unit) {
      toast.error("Unit not selected")
      return
    }
    if (values.unit && (values.stockQuantity === undefined || values.stockQuantity === null)) {
      toast.error("Stock quantity required when unit is selected")
      return
    }

    const { unit, ...rest } = values
    let finalStockQuantity = values.stockQuantity
    if (finalStockQuantity !== undefined && finalStockQuantity !== null && (finalStockQuantity as any) !== "") {
      const num = Number(finalStockQuantity)
      if (!isNaN(num)) {
        if (isIntegerUnit(unit)) {
          finalStockQuantity = Math.trunc(num)
        } else {
          finalStockQuantity = Number(num.toFixed(2))
        }
      }
    }

    const payload = {
      ...rest,
      stockQuantity: finalStockQuantity,
      measureUnit: unit || undefined,
    }

    if (variantToEdit) {
      updateVariant.mutate(payload, { onSuccess: () => onClose() })
    } else {
      addVariant.mutate(payload as any, { onSuccess: () => onClose() })
    }
  }

  const isPending = variantToEdit ? updateVariant.isPending : addVariant.isPending
  const isError = variantToEdit ? updateVariant.isError : addVariant.error
  const error = variantToEdit ? updateVariant.error : addVariant.error

  return (
    <section className="bg-[#be001e05] border border-[#be001e] rounded-xl p-5 space-y-4 shadow-[0_8px_30px_rgba(190,0,30,0.06)] animate-in fade-in duration-200 mb-6 md:mb-0">
      <div className="flex justify-between items-center pb-3 border-b border-[#be001e1a]">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-black tracking-wider text-[#be001e]">{variantToEdit ? "Edit Variant" : "Add New Variant"}</h3>
        </div>
        <button onClick={onClose} className="p-1 text-[#be001e] hover:bg-[#be001e0d] rounded-full transition-all cursor-pointer">
          <X className="size-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit as any, onInvalid)} noValidate className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field id="sku" label="SKU / Stock Keeping Unit *" error={errors.sku?.message}>
            <Input
              id="sku"
              placeholder="e.g. VAR-001-RED"
              {...register("sku")}
              className={cn(inputCls, errors.sku && "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/20")}
            />
          </Field>

          <Field id="name" label="Variant Name" error={errors.name?.message}>
            <Input
              id="name"
              placeholder="e.g. Red / Size M"
              {...register("name")}
              className={cn(inputCls, errors.name && "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/20")}
            />
          </Field>
        </div>

        {/* Row 1: Price + Currency */}
        <div className="grid grid-cols-1 min-[1000px]:grid-cols-2 gap-5">
          <div className="grid grid-cols-1 min-[450px]:grid-cols-4 gap-2 items-start">
            <div className="col-span-1 min-[450px]:col-span-3">
              <Field id="price" label="Price" error={errors.price?.message}>
                <Input
                  id="price"
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 99.99"
                  {...register("price", {
                    onChange: (e) => {
                      let val = e.target.value.replace(/[^0-9.]/g, "")
                      const parts = val.split(".")
                      if (parts.length > 2) {
                        val = parts[0] + "." + parts.slice(1).join("")
                      }
                      if (parts.length > 1 && parts[1].length > 2) {
                        val = parts[0] + "." + parts[1].slice(0, 2)
                      }
                      e.target.value = val
                      setValue("price", val === "" ? (undefined as any) : val, { shouldValidate: true })
                      trigger(["currencyCode", "price"])
                    },
                  })}
                  className={cn(
                    inputCls,
                    errors.price && "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/20"
                  )}
                  onKeyDown={blockFloatKeys}
                />
              </Field>
            </div>
            <div className="col-span-1">
              <Field id="currencyCode" label="Currency" error={errors.currencyCode?.message}>
                <div className="relative shrink-0 w-full" ref={currencyDropdownRef}>
                  <button
                    id="currencyCode"
                    type="button"
                    onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                    title={watch("currencyCode") || "Select"}
                    className={cn(
                      "w-full h-8 flex items-center justify-between gap-1 px-2.5 bg-slate-50 border-[0.5px] border-slate-200/60 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100/80 transition-colors cursor-pointer",
                      errors.currencyCode && "border-[#be001e] ring-1 ring-[#be001e]/20",
                      isCurrencyDropdownOpen && "border-[#be001e] ring-1 ring-[#be001e]/20"
                    )}
                  >
                    <span className="truncate">{watch("currencyCode") || "Select"}</span>
                    <ChevronDown className={cn("size-3 text-slate-400 shrink-0 transition-transform", isCurrencyDropdownOpen && "rotate-180")} />
                  </button>
                  {isCurrencyDropdownOpen && (
                    <div className="absolute left-0 right-0 min-[450px]:left-auto min-[450px]:right-0 mt-1.5 w-full min-[450px]:w-60 bg-white border border-slate-200 rounded-lg shadow-lg z-50 text-[12px] font-semibold text-slate-700 text-left overflow-hidden flex flex-col">
                      {/* Search Header - solid filled background */}
                      <div className="p-2 bg-slate-50 border-b border-slate-200 shrink-0">
                        <input
                          type="text"
                          value={currencySearch}
                          onChange={(e) => setCurrencySearch(e.target.value)}
                          placeholder="Search currency or country..."
                          className="w-full px-2.5 py-1 text-[11px] rounded bg-white border border-slate-200 outline-none focus:border-[#be001e] text-slate-700 placeholder:text-slate-400"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>

                      {/* Scrollable list strictly below the header */}
                      <div className="max-h-[160px] overflow-y-auto py-1">
                        {isLoadingCurrencies && (
                          <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                            <Loader2 className="size-3 animate-spin text-[#be001e]" />
                            <span>Loading currencies...</span>
                          </div>
                        )}
                        {filteredCurrencies.map((curr, idx) => (
                          <button
                            key={(curr as any).id ?? `${curr.code}-${idx}`}
                            type="button"
                            onClick={() => {
                              setValue("currencyCode", curr.code, { shouldValidate: true })
                              trigger(["currencyCode", "price"])
                              setIsCurrencyDropdownOpen(false)
                              setCurrencySearch("")
                            }}
                            title={curr.name}
                            className={cn(
                              "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate text-xs font-semibold block",
                              watch("currencyCode") === curr.code ? "text-[#be001e] bg-red-50/30 font-bold" : "text-slate-700"
                            )}
                          >
                            {curr.name}
                          </button>
                        ))}
                        {!isLoadingCurrencies && filteredCurrencies.length === 0 && (
                          <div className="px-3 py-2 text-[11px] text-slate-400 text-center">
                            No currency found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Field>
            </div>
          </div>

          {/* Spacer / placeholder so row stays at 2 cols on min-[1000px]+ */}
          <div className="hidden min-[1000px]:block" />
        </div>

        {/* Row 2: Stock Quantity + Unit and HSN Code */}
        <div className="grid grid-cols-1 min-[1000px]:grid-cols-2 gap-5">
          <div className="grid grid-cols-1 min-[450px]:grid-cols-4 gap-2 items-start">
            <div className="col-span-1 min-[450px]:col-span-3">
              <Field id="stockQuantity" label="Stock Quantity" error={errors.stockQuantity?.message}>
                <Input
                  id="stockQuantity"
                  type="text"
                  inputMode={isIntegerUnit(watch("unit")) ? "numeric" : "decimal"}
                  placeholder={isIntegerUnit(watch("unit")) ? "e.g. 100" : "e.g. 100.50"}
                  {...register("stockQuantity", {
                    onChange: (e) => {
                      const currentUnit = watch("unit")
                      let val = e.target.value
                      if (isIntegerUnit(currentUnit)) {
                        val = val.split(".")[0].replace(/\D/g, "")
                      } else {
                        val = val.replace(/[^0-9.]/g, "")
                        const parts = val.split(".")
                        if (parts.length > 2) {
                          val = parts[0] + "." + parts.slice(1).join("")
                        }
                        if (parts.length > 1 && parts[1].length > 2) {
                          val = parts[0] + "." + parts[1].slice(0, 2)
                        }
                      }
                      e.target.value = val
                      setValue("stockQuantity", val === "" ? (undefined as any) : val, { shouldValidate: true })
                      trigger(["unit", "stockQuantity"])
                    },
                  })}
                  className={cn(
                    inputCls,
                    errors.stockQuantity && "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/20"
                  )}
                  onKeyDown={(e) => blockQuantityKeys(e, watch("stockQuantity"), isIntegerUnit(watch("unit")))}
                />
              </Field>
            </div>
            <div className="col-span-1">
              <Field id="unit" label="Unit" error={errors.unit?.message}>
                <div className="relative shrink-0 w-full" ref={unitDropdownRef}>
                  <button
                    id="unit"
                    type="button"
                    onClick={() => setIsUnitDropdownOpen(!isUnitDropdownOpen)}
                    title={watch("unit") ? (QUANTITY_UNITS.find((u) => u.code === watch("unit"))?.name || watch("unit")) : "Select"}
                    className={cn(
                      "w-full h-8 flex items-center justify-between gap-1 px-2.5 bg-slate-50 border-[0.5px] border-slate-200/60 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100/80 transition-colors cursor-pointer",
                      errors.unit && "border-[#be001e] ring-1 ring-[#be001e]/20",
                      isUnitDropdownOpen && "border-[#be001e] ring-1 ring-[#be001e]/20"
                    )}
                  >
                    <span className="truncate">{watch("unit") || "Select"}</span>
                    <ChevronDown className={cn("size-3 text-slate-400 shrink-0 transition-transform", isUnitDropdownOpen && "rotate-180")} />
                  </button>
                  {isUnitDropdownOpen && (
                    <div className="absolute left-0 right-0 min-[450px]:left-auto min-[450px]:right-0 mt-1.5 w-full min-[450px]:w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[140px] overflow-y-auto py-1 text-[12px] font-semibold text-slate-700 text-left">
                      {QUANTITY_UNITS.map((u) => (
                        <button
                          key={u.code}
                          type="button"
                          onClick={() => {
                            const newUnit = u.code
                            setValue("unit", newUnit, { shouldValidate: true })
                            if (isIntegerUnit(newUnit)) {
                              const currentQty = watch("stockQuantity")
                              if (currentQty !== undefined && currentQty !== null && String(currentQty) !== "") {
                                const strQty = String(currentQty)
                                if (strQty.includes(".")) {
                                  const trimmed = strQty.split(".")[0].replace(/\D/g, "")
                                  setValue("stockQuantity", trimmed === "" ? (undefined as any) : trimmed, { shouldValidate: true })
                                }
                              }
                            }
                            trigger(["unit", "stockQuantity"])
                            setIsUnitDropdownOpen(false)
                          }}
                          title={u.name}
                          className={cn(
                            "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate text-xs font-semibold",
                            watch("unit") === u.code ? "text-[#be001e] bg-red-50/30 font-bold" : "text-slate-700"
                          )}
                        >
                          {u.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Field>
            </div>
          </div>

          <Field id="hsnCode" label="HSN Code" error={errors.hsnCode?.message}>
            <Input
              id="hsnCode"
              placeholder="e.g. 0910"
              {...register("hsnCode")}
              className={cn(inputCls, errors.hsnCode && "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/20")}
            />
          </Field>
        </div>

        <Field id="description" label="Description" error={errors.description?.message} full>
          <textarea
            id="description"
            rows={3}
            placeholder="Variant-specific details like color, size, material, dimensions…"
            {...register("description")}
            className={cn(
              "w-full resize-none rounded-lg border-[0.5px] border-slate-200/60 bg-slate-50 px-3 py-2.5 text-xs text-[#291716] outline-none placeholder:text-slate-400 focus:border-[#be001e]",
              errors.description && "border-[#be001e] ring-1 ring-[#be001e]/20"
            )}
          />
        </Field>

        <Controller
          control={control}
          name="tags"
          render={() => (
            <Field id="tag-input-variant" label="Tags" error={errors.tags?.message} full>
              <div className="relative flex items-center">
                <input
                  id="tag-input-variant"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder='Type & press Enter'
                  className="w-full pl-3 pr-20 py-2 bg-slate-50 border-[0.5px] border-slate-200/60 rounded-lg text-xs text-[#291716] focus:border-[#be001e] outline-none transition-colors placeholder:text-slate-400"
                />
                <button type="button" onClick={addTag} className="absolute right-1 px-4 py-1 bg-[#fff0ef] text-[#be001e] font-bold text-xs border-[0.5px] border-[#e7bdb9]/40 rounded-md hover:bg-[#be001e]/5 transition-colors cursor-pointer">
                  Add
                </button>
              </div>
              {tags.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1">
                  {tags.map((t) => (
                    <span key={t} className="flex items-center gap-1 rounded-full border border-[#e7bdb9]/50 bg-[#fff0ef] px-2 py-0.5 text-[10px] font-semibold text-[#be001e]">
                      {t}
                      <button type="button" onClick={() => removeTag(t)} className="hover:text-red-800 cursor-pointer"><X className="size-2.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </Field>
          )}
        />

        <div className="flex justify-end items-center gap-3 pt-4 border-t-[0.5px] border-[#be001e1a]">
          <button type="button" onClick={onClose} className="px-5 py-2 border-[0.5px] border-slate-200 text-slate-500 rounded-lg text-xs font-semibold hover:bg-slate-50 cursor-pointer">
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isPending}
            className="px-5 h-8 bg-[#e6202f] hover:bg-[#c91c29] text-white rounded-lg text-xs font-bold transition-all shadow-sm cursor-pointer"
          >
            {isPending
              ? <span className="flex items-center gap-2"><Loader2 className="size-3.5 animate-spin" /> Saving…</span>
              : variantToEdit ? "Update Variant" : "Save Variant"}
          </Button>
        </div>
      </form>
    </section>
  )
}

export function VariantDetailView({
  productId,
  variantId,
  onBack,
}: {
  productId?: string
  variantId: string
  onBack: () => void
}) {
  const { data: variant, isLoading: isVariantLoading, isError: isVariantError, error: variantError } = useProductVariantDetails(variantId)
  const [showEditVariant, setShowEditVariant] = useState(false)
  const [showActionsDropdown, setShowActionsDropdown] = useState(false)
  const sidebarCollapsed = useSellerUIStore((s) => s.sidebarCollapsed)
  const resolvedProductId = productId || variant?.productId

  if (isVariantLoading) {
    return (
      <div className="relative bg-white border-[0.5px] border-slate-200/60 rounded-xl p-5 md:p-6 shadow-sm min-h-[280px] flex flex-col items-center justify-center gap-3">
        <div className="absolute top-0 left-0 w-full h-1 bg-[#be001e] rounded-t-xl" />
        <Loader2 className="size-8 animate-spin text-[#be001e]" />
        <p className="text-sm font-semibold text-slate-400">Loading variant details…</p>
      </div>
    )
  }

  if (isVariantError || !variant) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs text-[#be001e] font-semibold">
        {(variantError as Error)?.message || "Variant not found."}
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs font-bold text-slate-500 transition-colors hover:text-[#be001e] cursor-pointer"
        >
          <ArrowLeft className="size-4" /> Back to Product
        </button>
      </div>

      {showEditVariant ? (
        <div className="space-y-4">
          <AddVariantForm
            productId={resolvedProductId || ""}
            variantToEdit={variant}
            onClose={() => setShowEditVariant(false)}
          />
          {/* Empty space below form on mobile */}
          <div className="h-28 md:hidden" aria-hidden="true" />
        </div>
      ) : (
        <section className="bg-white border-[0.5px] border-slate-200/60 rounded-xl p-6 relative overflow-hidden flex flex-col md:flex-row gap-6 items-start group">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#be001e]" />
          <div className="absolute top-4 right-4 z-10">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                className="p-1.5 text-slate-500 hover:text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/30 rounded-lg transition-all border border-slate-200 cursor-pointer flex items-center justify-center"
                title="Actions"
              >
                <MoreVertical className="size-4" />
              </button>
              {showActionsDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-30" 
                    onClick={() => setShowActionsDropdown(false)} 
                  />
                  <div className="absolute right-0 mt-1 w-25 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setShowActionsDropdown(false)
                        setShowEditVariant(true)
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5 cursor-pointer"
                    >
                      <Edit2 className="size-3.5 text-slate-500" />
                      Edit
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="w-16 h-16 bg-[#fff0ef] border-[0.5px] border-[#e7bdb9]/30 rounded-lg flex items-center justify-center text-[#be001e] shrink-0">
            <Layers className="size-8" />
          </div>
          <div className="flex-1 space-y-4 w-full">
            <div>
              <h2 className="text-xl font-black text-[#291716] tracking-tight mb-1">{variant.name || "Unnamed Variant"}</h2>
              {variant.description && <p className="text-xs text-slate-500 leading-relaxed max-w-2xl">{variant.description}</p>}
            </div>

            <div
              className={cn(
                "grid gap-3 mt-4",
                sidebarCollapsed
                  ? "grid-cols-1 md:grid-cols-2"
                  : "grid-cols-1 lg:grid-cols-2"
              )}
            >
              {[
                { icon: Hash, label: "SKU", value: variant.sku },
                { icon: FileText, label: "HSN Code", value: variant.hsnCode },
                { 
                  icon: Package, 
                  label: "Stock", 
                  value: variant.stockQuantity !== undefined 
                    ? `${variant.stockQuantity}${variant.measureUnit || variant.unit ? ` ${variant.measureUnit || variant.unit}` : ""}` 
                    : undefined 
                },
                { icon: Calendar, label: "Price", value: variant.price !== undefined ? `${variant.currencyCode || "INR"} ${variant.price}` : undefined },
              ].map(({ icon: Icon, label, value }) =>
                value ? (
                  <div key={label} className="bg-slate-50/50 border-[0.5px] border-slate-200/60 p-2.5 md:p-3 rounded-xl flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-1 text-[#be001e] min-w-0">
                      <Icon className="size-3.5 shrink-0" />
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#5d3f3d]">{label}</span>
                    </div>
                    <p className="text-xs font-bold text-[#291716] break-words whitespace-normal" title={value}>{value}</p>
                  </div>
                ) : null
              )}
            </div>

            {variant.tags && variant.tags.length > 0 && (
              <div className="flex items-center gap-3 pt-2">
                <div className="flex items-center gap-1 text-[#5d3f3d] text-[10px]">
                  <Tag className="size-3.5 text-[#be001e]" />
                  <span className="font-bold uppercase tracking-wider">Tags</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {variant.tags.map((t, i) => (
                    <span key={i} className="px-2.5 py-0.5 bg-[#be001e]/5 border-[0.5px] border-[#be001e]/20 rounded-full text-[10px] font-semibold text-[#be001e]">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  )
}

function ProductImageCarousel({ images, productName }: { images?: ProductImage[]; productName: string }) {
  const [currentIndex, setCurrentIndex] = useState(0)

  if (!images || images.length === 0) {
    return (
      <div className="w-full sm:w-44 sm:h-44 md:w-48 md:h-48 h-56 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-50 shadow-sm relative flex flex-col items-center justify-center gap-1.5 text-slate-400 font-sans">
        <Package className="size-10 text-slate-350" />
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">No Image</span>
      </div>
    )
  }

  if (images.length === 1) {
    return (
      <div className="w-full sm:w-44 sm:h-44 md:w-48 md:h-48 h-56 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-50 shadow-sm relative group/single">
        <img
          src={images[0].url}
          alt={productName}
          className="w-full h-full object-cover group-hover/single:scale-105 transition-transform duration-200"
        />
        {(images[0].caption || (images[0].order !== undefined && images[0].order !== null)) && (
          <div className="absolute bottom-0 inset-x-0 bg-black/75 text-white text-[9px] px-2 py-1 flex items-center justify-between gap-1 font-sans font-semibold">
            <span className="truncate">{images[0].caption || "Image 1"}</span>
            {images[0].order !== undefined && images[0].order !== null && (
              <span className="shrink-0 bg-white/20 px-1 rounded text-[8px] font-mono">Order: {images[0].order}</span>
            )}
          </div>
        )}
      </div>
    )
  }

  const currentImage = images[currentIndex] || images[0]

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="relative group/carousel shrink-0 w-full sm:w-44 sm:h-44 md:w-48 md:h-48 h-56 rounded-xl overflow-hidden border border-slate-200 bg-slate-900 shadow-md flex flex-col justify-between select-none">
      <img
        src={currentImage.url}
        alt={currentImage.caption || productName}
        className="w-full h-full object-cover transition-all duration-300"
      />

      {/* Navigation Overlay Arrows */}
      <button
        type="button"
        onClick={prevImage}
        className="absolute left-1.5 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all cursor-pointer opacity-90 hover:opacity-100 shadow z-10"
        title="Previous Image"
      >
        <ChevronLeft className="size-4" />
      </button>

      <button
        type="button"
        onClick={nextImage}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all cursor-pointer opacity-90 hover:opacity-100 shadow z-10"
        title="Next Image"
      >
        <ChevronRight className="size-4" />
      </button>

      {/* Caption & Indicator Badge */}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 pt-4 flex items-end justify-between gap-1 text-white pointer-events-none z-10">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[10px] font-semibold truncate leading-tight">
            {currentImage.caption || `Image ${currentIndex + 1}`}
          </span>
          {currentImage.order !== undefined && currentImage.order !== null && (
            <span className="text-[8px] text-slate-300 font-mono">
              Order: {currentImage.order}
            </span>
          )}
        </div>
        <span className="text-[9px] font-mono bg-black/60 px-1.5 py-0.5 rounded-md shrink-0 font-bold self-end">
          {currentIndex + 1}/{images.length}
        </span>
      </div>

      {/* Slide dots */}
      <div className="absolute top-2 inset-x-0 flex justify-center gap-1 z-10">
        {images.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setCurrentIndex(idx)
            }}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              idx === currentIndex ? "w-4 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  )
}

function ProductDetailView({
  productId,
  onBack,
}: {
  productId: string
  onBack: () => void
}) {
  const { data, isLoading, isError, error } = useProductDetails(productId)
  const [showAddVariant, setShowAddVariant] = useState(false)
  const [showEditProduct, setShowEditProduct] = useState(false)
  const [showActionsDropdown, setShowActionsDropdown] = useState(false)
  const [showEditProductDropdown, setShowEditProductDropdown] = useState(false)
  const sidebarCollapsed = useSellerUIStore((s) => s.sidebarCollapsed)
  const navigate = useNavigate()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs font-bold text-slate-500 transition-colors hover:text-[#be001e] cursor-pointer"
        >
          <ArrowLeft className="size-4" /> Back to Products
        </button>


      </div>

      {isLoading && (
        <div className="relative bg-white border-[0.5px] border-slate-200/60 rounded-xl p-5 md:p-6 shadow-sm min-h-[280px] flex flex-col items-center justify-center gap-3">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#be001e] rounded-t-xl" />
          <Loader2 className="size-8 animate-spin text-[#be001e]" />
          <p className="text-sm font-semibold text-slate-400">Loading product…</p>
        </div>
      )}
      {isError && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs text-[#be001e] font-semibold">
          {(error as Error).message}
        </div>
      )}
      {data && (
        <>
          {showEditProduct ? (
            <AddProductForm
              companyId={data.companyId}
              productToEdit={data}
              onClose={() => setShowEditProduct(false)}
            />
          ) : (
            <section className="bg-white border-[0.5px] border-slate-200/60 rounded-xl p-5 md:p-6 relative overflow-hidden group shadow-sm">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#be001e]" />
              
              <div className="flex flex-col sm:flex-row gap-5 md:gap-6 items-start">
                <ProductImageCarousel images={data.images} productName={data.productName} />
                
                <div className="flex-1 min-w-0 space-y-4 w-full">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-xl md:text-2xl font-black text-[#291716] tracking-tight mb-1 break-words">
                        {data.productName}
                      </h2>
                      {data.description && (
                        <p className="text-xs text-slate-500 leading-relaxed max-w-2xl font-normal">
                          {data.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowEditProductDropdown(!showEditProductDropdown)}
                        className="p-1.5 text-slate-500 hover:text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/30 rounded-lg transition-all border border-slate-200 cursor-pointer flex items-center justify-center"
                        title="Actions"
                      >
                        <MoreVertical className="size-4" />
                      </button>
                      {showEditProductDropdown && (
                        <>
                          <div 
                            className="fixed inset-0 z-30" 
                            onClick={() => setShowEditProductDropdown(false)} 
                          />
                          <div className="absolute right-0 mt-1 w-20 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => {
                                setShowEditProductDropdown(false)
                                setShowEditProduct(true)
                              }}
                              className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5 cursor-pointer"
                            >
                              <Edit2 className="size-3.5 text-slate-500" />
                              Edit
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    className={cn(
                      "grid gap-3 mt-4",
                      sidebarCollapsed
                        ? "grid-cols-1 md:grid-cols-2"
                        : "grid-cols-1 lg:grid-cols-2"
                    )}
                  >
                    {[
                      { icon: Hash, label: "Product Code", value: data.productCode },
                      { icon: Layers, label: "Category", value: data.productCategory },
                      { icon: Layers, label: "Sub-Category", value: data.productSubCategory },
                      { icon: Calendar, label: "Added", value: new Date(data.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) },
                    ].map(({ icon: Icon, label, value }) =>
                      value ? (
                        <div key={label} className="bg-slate-50/70 border-[0.5px] border-slate-200/60 p-2.5 md:p-3 rounded-xl flex flex-col gap-1 min-w-0">
                          <div className="flex items-center gap-1 text-[#be001e] min-w-0">
                            <Icon className="size-3.5 shrink-0" />
                            <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#5d3f3d]">{label}</span>
                          </div>
                          <p className="text-xs font-bold text-[#291716] break-words whitespace-normal" title={value}>{value}</p>
                        </div>
                      ) : null
                    )}
                  </div>

                  {data.tags?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 pt-2">
                      <div className="flex items-center gap-1 text-[#5d3f3d] text-[10px] shrink-0">
                        <Tag className="size-3.5 text-[#be001e]" />
                        <span className="font-bold uppercase tracking-wider">Tags</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {data.tags.map((t) => (
                          <span key={t} className="px-2.5 py-0.5 bg-[#be001e]/5 border-[0.5px] border-[#be001e]/20 rounded-full text-[10px] font-semibold text-[#be001e]">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {showAddVariant && !showEditProduct && (
            <AddVariantForm
              productId={productId}
              product={data}
              onClose={() => setShowAddVariant(false)}
            />
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="size-4 text-[#be001e]" />
                <h4 className="text-sm font-bold text-[#291716]">
                  Variants {data.variants ? `(${data.variants.length})` : "(0)"}
                </h4>
              </div>

              {data && !showAddVariant && !showEditProduct && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                    className="p-1.5 text-slate-500 hover:text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/30 rounded-lg transition-all border border-slate-200 cursor-pointer flex items-center justify-center"
                    title="Actions"
                  >
                    <MoreVertical className="size-4" />
                  </button>
                  {showActionsDropdown && (
                    <>
                      <div 
                        className="fixed inset-0 z-30" 
                        onClick={() => setShowActionsDropdown(false)} 
                      />
                      <div className="absolute right-0 mt-1 w-20 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => {
                            setShowActionsDropdown(false)
                            setShowAddVariant(true)
                          }}
                          className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5 cursor-pointer"
                        >
                          + Variant
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {!data.variants || data.variants.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center shadow-sm">
                <p className="text-xs text-slate-500 font-medium">No variants added for this product yet.</p>
                <button
                  onClick={() => setShowAddVariant(true)}
                  className="mt-2 text-xs font-bold text-[#be001e] hover:underline cursor-pointer"
                >
                  Add the first variant
                </button>
              </div>
            ) : (
              <div
                className={cn(
                  "grid gap-4",
                  sidebarCollapsed
                    ? "grid-cols-1 md:grid-cols-2"
                    : "grid-cols-1 lg:grid-cols-2"
                )}
              >
                {data.variants.map((variant) => (
                  <VariantCard
                    key={variant.id}
                    variant={variant}
                    onViewDetail={() =>
                      navigate({
                        to: "/products/variant/$variantId",
                        params: { variantId: variant.id },
                      })
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
